/**
 * skillDraft — pure helpers for editing a SKILL.md's `routes:` block (ADR-094)
 * from the structured Skills Studio editor, WITHOUT reformatting the rest of the
 * document. A SKILL.md is hand-authored (comments, key order, quoting matter), so
 * we splice only the `routes:` block in the frontmatter rather than round-trip
 * the whole thing through a YAML dumper. DOM-free and unit-tested.
 */
import * as yaml from 'js-yaml'
import type { SkillRoute } from '../api/client'

// Matches a leading `---\n<frontmatter>\n---` block, capturing the pieces so the
// body (and the exact opening/closing fences) are preserved verbatim.
// groups: 1=BOM? 2=`---\n` 3=frontmatter body 4=`\n---` 5=rest (body, incl leading newline)
const FRONTMATTER_RE = /^(﻿?)(---\r?\n)([\s\S]*?)(\r?\n---)([\s\S]*)$/

/** Extract the `routes:` array from a SKILL.md's frontmatter. Never throws. */
export function parseRoutesFromRaw(raw: string): SkillRoute[] {
  const m = raw.match(FRONTMATTER_RE)
  if (!m) return []
  let fm: unknown
  try { fm = yaml.load(m[3]) } catch { return [] }
  if (!fm || typeof fm !== 'object') return []
  const routes = (fm as Record<string, unknown>).routes
  if (!Array.isArray(routes)) return []
  const out: SkillRoute[] = []
  for (const r of routes) {
    if (!r || typeof r !== 'object') continue
    const rr = r as Record<string, unknown>
    out.push({
      intent: typeof rr.intent === 'string' ? rr.intent : '',
      workflow: typeof rr.workflow === 'string' ? rr.workflow : '',
      inputs: rr.inputs && typeof rr.inputs === 'object' ? (rr.inputs as Record<string, string>) : undefined,
    })
  }
  return out
}

/**
 * Return `raw` with its frontmatter `routes:` block replaced by `routes` (or
 * removed entirely when `routes` is empty). Everything else — other frontmatter
 * keys, comments, the Markdown body — is preserved byte-for-byte. If `raw` has
 * no frontmatter, it is returned unchanged (the structured editor is disabled in
 * that case).
 */
export function setRoutesInSkillMd(raw: string, routes: SkillRoute[]): string {
  const m = raw.match(FRONTMATTER_RE)
  if (!m) return raw
  const [, bom, open, front, close, rest] = m
  const stripped = removeRoutesBlock(front).replace(/[ \t\r\n]*$/, '') // drop trailing blank space
  const block = routes.length ? serializeRoutes(routes) : ''
  const newFront = block ? `${stripped}\n${block}` : stripped
  return `${bom}${open}${newFront}${close}${rest}`
}

// removeRoutesBlock drops a top-level `routes:` key and its indented children
// (and any blank lines inside the block) from a frontmatter string, leaving
// every other line untouched.
function removeRoutesBlock(front: string): string {
  const lines = front.split('\n')
  const out: string[] = []
  let skipping = false
  for (const line of lines) {
    if (skipping) {
      if (line.trim() === '' || /^\s/.test(line)) continue // still inside the block
      skipping = false // a new top-level (unindented) key ends it
    }
    if (/^routes\s*:/.test(line)) { skipping = true; continue }
    out.push(line)
  }
  return out.join('\n')
}

// serializeRoutes renders a `routes:` block. Intents/input values are emitted as
// JSON-style double-quoted scalars (valid YAML) so arbitrary text is safe;
// workflow names are kebab tokens emitted bare.
function serializeRoutes(routes: SkillRoute[]): string {
  const lines = ['routes:']
  for (const r of routes) {
    lines.push(`  - intent: ${JSON.stringify(r.intent ?? '')}`)
    lines.push(`    workflow: ${r.workflow ?? ''}`)
    if (r.inputs && Object.keys(r.inputs).length > 0) {
      const pairs = Object.entries(r.inputs).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(', ')
      lines.push(`    inputs: { ${pairs} }`)
    }
  }
  return lines.join('\n')
}

/** One line of a diff: unchanged context, an addition, or a deletion. */
export type DiffLine = { type: 'ctx' | 'add' | 'del'; text: string }

/**
 * A line-level diff of `oldText` → `newText` (classic LCS). Pure. Backs the
 * Skills Studio's "review changes before proposing" view.
 */
export function lineDiff(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split('\n')
  const b = newText.split('\n')
  const n = a.length
  const m = b.length
  // dp[i][j] = LCS length of a[i:] and b[j:]
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }
  const out: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ type: 'ctx', text: a[i] }); i++; j++ }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: 'del', text: a[i] }); i++ }
    else { out.push({ type: 'add', text: b[j] }); j++ }
  }
  while (i < n) { out.push({ type: 'del', text: a[i] }); i++ }
  while (j < m) { out.push({ type: 'add', text: b[j] }); j++ }
  return out
}

/** The outcome of ensuring a SKILL.md frontmatter pins an alias. */
export interface EnsureSkillPinResult {
  raw: string
  /** Set when the pin could NOT be established, and why. The caller must not insert. */
  refusal?: string
}

/**
 * Return `raw` with its frontmatter pinning `alias` to `setRef` (ADR-126 D2).
 *
 * WHY THE PICKER HAS TO DO THIS. A skill body reference with no pin is a validation
 * error, in the validator's own words: "a skill declares its pin in frontmatter
 * exactly as an activity does (ADR-126 D2)". ADR-127 D15 makes that the picker's
 * responsibility rather than the operator's — the workflow editor's counterpart is
 * `ensureDataPin` in workflowDraft.ts, and this is the same contract for the surface
 * where the pin lives in frontmatter instead of at a YAML top level.
 *
 * SPLICED, NOT ROUND-TRIPPED, for the reason this module already gives: a SKILL.md
 * is hand-authored, so comments, key order and quoting matter. Only the `data:`
 * entry is touched; everything else is preserved byte-for-byte.
 *
 * It REFUSES rather than guessing in every case it cannot do safely — a corrupted
 * frontmatter is far worse than an un-inserted token:
 *
 *   no frontmatter        there is nowhere for a pin to live
 *   unparseable YAML      any splice would be into a document we cannot read
 *   `data:` in FLOW form  (`data: {a: b}`) — appending an indented child would
 *                         produce invalid YAML
 *   alias bound elsewhere repointing it would change what every existing
 *                         {{data.<alias>.…}} in the body resolves to
 */
export function ensureDataPinInSkillMd(
  raw: string,
  alias: string,
  setRef: string,
): EnsureSkillPinResult {
  const m = raw.match(FRONTMATTER_RE)
  if (!m) {
    return { raw, refusal: 'This file has no frontmatter, so there is nowhere to declare the '
      + 'pin a reference needs (ADR-126 D2). Add a `---` frontmatter block first.' }
  }
  const [, bom, open, front, close, rest] = m

  let parsed: unknown
  try { parsed = yaml.load(front) } catch {
    return { raw, refusal: 'The frontmatter is not valid YAML, so the pin cannot be added '
      + 'safely. Fix the frontmatter first — a blind splice would corrupt it.' }
  }
  const block = (parsed && typeof parsed === 'object')
    ? (parsed as Record<string, unknown>).data : undefined

  if (block !== undefined && (typeof block !== 'object' || block === null || Array.isArray(block))) {
    return { raw, refusal: `The frontmatter's \`data:\` is a ${Array.isArray(block) ? 'list' : typeof block}, `
      + 'not a mapping of alias to set. Fix it before inserting a reference.' }
  }

  if (block) {
    const existing = (block as Record<string, unknown>)[alias]
    const pinned = typeof existing === 'string'
      ? existing
      : (existing && typeof existing === 'object'
        ? (existing as Record<string, unknown>).set : undefined)
    if (typeof pinned === 'string') {
      if (pinned === setRef) return { raw }          // already pinned, nothing to do
      return { raw, refusal: `This skill already pins "${alias}" to ${pinned}. Inserting a `
        + `reference to ${setRef} through the same alias would change what every existing `
        + `{{data.${alias}.…}} in the body resolves to. Rename one of them first.` }
    }
  }

  // Find a top-level `data:` line to extend. A FLOW mapping on that line cannot take
  // an indented child, so it is refused rather than corrupted.
  const lines = front.split('\n')
  const index = lines.findIndex(line => /^data\s*:/.test(line))
  if (index === -1) {
    const stripped = front.replace(/[ \t\r\n]*$/, '')
    return { raw: `${bom}${open}${stripped}\ndata:\n  ${alias}: ${setRef}${close}${rest}` }
  }
  if (lines[index].replace(/^data\s*:/, '').trim() !== '') {
    return { raw, refusal: 'The frontmatter writes `data:` inline (flow style). Adding a pin '
      + 'under it would produce invalid YAML — rewrite it as a block mapping first.' }
  }
  const next = [...lines.slice(0, index + 1), `  ${alias}: ${setRef}`, ...lines.slice(index + 1)]
  return { raw: `${bom}${open}${next.join('\n')}${close}${rest}` }
}
